import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, Clock, Zap, Users, TrendingUp, Shield, ChevronRight, HelpCircle,
  Eye, AlarmClock, LineChart, Activity, Bell,
  ListChecks, BellRing, Trophy, Target,
  Smartphone, LayoutDashboard, Flame, AlertTriangle, ArrowUpRight, CheckCircle2,
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import heroImage from '@/assets/hero-bg.jpg';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

export default function Landing() {
  // Auth context intentionally unused on the landing hero.

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <nav className="border-b border-border/50 backdrop-blur-sm sticky top-0 z-50 bg-background/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-[15px] flex items-center justify-between">
          <Link to="/" aria-label="PullOver.App home" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><Logo variant="full" className="h-12 sm:h-14 object-contain" /></Link>
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/pricing">Pricing</Link>
            </Button>
            <Link to="/login">
              <Button variant="ghost" size="sm">Sign In</Button>
            </Link>
            <Link to="/login">
              <Button size="sm">Book Demo</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(to bottom, hsl(var(--background) / 0.60) 0%, hsl(var(--background) / 0.40) 50%, hsl(var(--background) / 0.25) 85%, hsl(var(--background) / 0.25) 100%)' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24 text-center relative">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <span className="inline-block px-3 sm:px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold tracking-wide uppercase bg-primary/10 text-primary border border-primary/20 mb-5 sm:mb-6">
              Your Sales Floor in 30 Seconds
            </span>
          </motion.div>
          <motion.h1
            className="font-display text-[2.25rem] leading-[1.05] sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5 sm:mb-6 text-balance"
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
          >
            Fortune Is In The <span className="text-primary whitespace-nowrap">Follow-Up!</span>
          </motion.h1>
          <motion.p
            className="text-base sm:text-xl text-muted-foreground max-w-[700px] mx-auto mb-4 leading-relaxed text-pretty"
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
          >
            <span className="font-bold text-primary tracking-tight block mb-5 text-pretty">
              WARNING: <span className="text-foreground">Pull</span> Over <span className="text-foreground">Sales</span> is not for dealers running on sticky notes, scratch papers, spread sheets, memory and mere guesswork!
            </span>
            <span className="block text-pretty">
              Built for Sales Pros and Managers who need every deal, demo, <span className="whitespace-nowrap">follow-up,</span> and next step visible before an opportunity disappears forever.
            </span>
          </motion.p>
          <motion.p
            className="text-sm text-muted-foreground max-w-[640px] mx-auto mb-8 sm:mb-10"
            initial="hidden" animate="visible" variants={fadeUp} custom={3}
          >
            Faster than paper. Sharper than memory. Not another CRM.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center max-w-md sm:max-w-none mx-auto"
            initial="hidden" animate="visible" variants={fadeUp} custom={4}
          >
            <Link to="/login?tab=demo" className="w-full sm:w-auto"><Button size="lg" className="w-full sm:w-auto text-base px-8">Try the Demo</Button></Link>
            <Link to="/login?tab=real" className="w-full sm:w-auto"><Button size="lg" variant="outline" className="w-full sm:w-auto text-base px-8">Sign In</Button></Link>
            <a href="#how-it-works" className="w-full sm:w-auto"><Button size="lg" variant="ghost" className="w-full sm:w-auto text-base px-8">How It Works <ChevronRight className="ml-1 h-4 w-4" /></Button></a>
          </motion.div>

        </div>
      </section>

      {/* Social proof strip */}
      <section className="py-10 border-t border-border/30 bg-card/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-[1.75rem] sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] text-center mb-3 text-balance">
            Turn <span className="text-primary">Attention</span> Into <span className="text-primary">Sales!</span>
          </h2>
          <h3 className="text-center text-lg sm:text-2xl font-bold text-muted-foreground mb-6 text-balance">
            Your <span className="text-primary">Entire Pipeline</span> Organized In Seconds!
          </h3>
          <p className="text-center text-xs uppercase tracking-widest text-muted-foreground mb-6">
            Built for modern automotive sales teams
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
            {['Northgate Auto Group', 'Coastal Motors', 'Summit Dealer Co.', 'Metro Auto Collective', 'Pacific Drive Group'].map(name => (
              <span key={name} className="text-sm font-semibold tracking-wide text-muted-foreground/80 uppercase">
                {name}
              </span>
            ))}
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/30 text-xs font-semibold">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
              </span>
              Pilot Program Active
            </span>
          </div>
        </div>
      </section>

      {/* Product UI Proof */}
      <section id="product" className="py-20 border-t border-border/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl font-bold mb-3">Built for the floor, not the back office.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">A real product running in real dealerships — designed for two-tap logging on mobile and 30-second visibility on desktop.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Manager Dashboard mockup */}
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-elegant">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-background/40">
                <span className="h-3 w-3 rounded-full bg-red-500/70" />
                <span className="h-3 w-3 rounded-full bg-yellow-500/70" />
                <span className="h-3 w-3 rounded-full bg-green-500/70" />
                <span className="ml-3 text-xs text-muted-foreground">Manager Dashboard · pullover.app</span>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { l: 'Active Deals', v: '47' },
                    { l: 'Demos Today', v: '12' },
                    { l: 'Stalled', v: '8', red: true },
                    { l: 'Closed MTD', v: '23' },
                  ].map(k => (
                    <div key={k.l} className="rounded-lg border border-border bg-background/40 p-3">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.l}</div>
                      <div className={`text-2xl font-bold ${k.red ? 'text-primary' : 'text-foreground'}`}>{k.v}</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-border bg-background/40">
                  <div className="px-3 py-2 border-b border-border text-xs font-semibold text-muted-foreground flex items-center justify-between">
                    <span>Live Deal Activity</span>
                    <span className="text-primary inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> 3 stalling</span>
                  </div>
                  {[
                    { c: 'Patel, R.', r: 'Marcus', s: 'Test Drive', t: '12m ago', ok: true },
                    { c: 'Nguyen, T.', r: 'Sasha', s: 'Negotiating', t: '4h ago', ok: true },
                    { c: 'Williams, J.', r: 'Devon', s: 'Follow-up Due', t: '26h ago', ok: false },
                    { c: 'Garcia, M.', r: 'Marcus', s: 'Sold', t: '1d ago', ok: true },
                  ].map(r => (
                    <div key={r.c} className="flex items-center justify-between px-3 py-2 text-xs border-t border-border/50 first:border-t-0">
                      <span className="font-medium text-foreground w-1/3 truncate">{r.c}</span>
                      <span className="text-muted-foreground w-1/4">{r.r}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.ok ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>{r.s}</span>
                      <span className="text-muted-foreground text-[10px] w-16 text-right">{r.t}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-5 py-3 border-t border-border bg-background/30 text-xs text-muted-foreground flex items-center gap-2">
                <LayoutDashboard className="h-3.5 w-3.5" /> Manager Dashboard — real-time floor visibility
              </div>
            </div>

            {/* Leaderboard mockup */}
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-elegant">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-background/40">
                <span className="h-3 w-3 rounded-full bg-red-500/70" />
                <span className="h-3 w-3 rounded-full bg-yellow-500/70" />
                <span className="h-3 w-3 rounded-full bg-green-500/70" />
                <span className="ml-3 text-xs text-muted-foreground">Leaderboard · This Week</span>
              </div>
              <div className="p-5 space-y-2">
                {[
                  { rank: 1, name: 'Marcus T.', score: 248, demos: 14, fr: '92%', up: true },
                  { rank: 2, name: 'Sasha P.', score: 221, demos: 12, fr: '88%', up: true },
                  { rank: 3, name: 'Devon K.', score: 197, demos: 10, fr: '74%', up: false },
                  { rank: 4, name: 'Riley M.', score: 164, demos: 8, fr: '69%', up: false },
                  { rank: 5, name: 'Jordan S.', score: 142, demos: 7, fr: '81%', up: true },
                ].map(r => (
                  <div key={r.rank} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background/40">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${r.rank === 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>{r.rank}</div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-foreground">{r.name}</div>
                      <div className="text-[11px] text-muted-foreground">{r.demos} demos · {r.fr} follow-up</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-foreground">{r.score}</div>
                      <div className={`text-[10px] inline-flex items-center gap-0.5 ${r.up ? 'text-primary' : 'text-muted-foreground'}`}>
                        <ArrowUpRight className={`h-3 w-3 ${r.up ? '' : 'rotate-90'}`} />{r.up ? '+12%' : '-4%'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 border-t border-border bg-background/30 text-xs text-muted-foreground flex items-center gap-2">
                <Trophy className="h-3.5 w-3.5" /> Leaderboard — fair scoring on activity, not luck
              </div>
            </div>

            {/* Rep Dashboard (mobile) */}
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-elegant">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-background/40">
                <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Rep Dashboard · iPhone</span>
              </div>
              <div className="p-5">
                <div className="mx-auto max-w-[280px] rounded-[28px] border border-border bg-background/60 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Today</div>
                      <div className="text-lg font-bold">Hi, Marcus</div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-semibold inline-flex items-center gap-1"><Flame className="h-3 w-3" /> 6-day streak</span>
                  </div>
                  {[
                    { c: 'Patel, R.', s: 'Send numbers', due: 'Today 2:00pm', hot: true },
                    { c: 'Nguyen, T.', s: 'Follow-up call', due: 'Today 4:30pm', hot: false },
                    { c: 'Lee, A.', s: 'Appointment', due: 'Tomorrow', hot: false },
                  ].map(d => (
                    <div key={d.c} className="rounded-lg border border-border bg-card p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">{d.c}</span>
                        {d.hot && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-bold">HOT</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{d.s} · {d.due}</div>
                    </div>
                  ))}
                  <button className="w-full bg-primary text-primary-foreground rounded-full py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2">
                    <Zap className="h-4 w-4" /> Quick Log
                  </button>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-border bg-background/30 text-xs text-muted-foreground flex items-center gap-2">
                <Smartphone className="h-3.5 w-3.5" /> Rep Dashboard — built for one-handed use on the lot
              </div>
            </div>

            {/* Quick Log */}
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-elegant">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-background/40">
                <Zap className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs text-muted-foreground">Quick Log · 10-second activity</span>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Activity Type</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { l: 'Walkaround', a: true },
                      { l: 'Test Drive', a: false },
                      { l: 'Follow-up', a: false },
                      { l: 'Phone Call', a: false },
                      { l: 'Email', a: false },
                    ].map(t => (
                      <span key={t.l} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${t.a ? 'bg-primary text-primary-foreground border-primary' : 'bg-background/40 text-muted-foreground border-border'}`}>{t.l}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Customer</div>
                  <div className="px-3 py-2 rounded-lg border border-border bg-background/40 text-sm">Patel, R. — 2024 Range Rover Sport</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Next Step (required)</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { l: 'Send numbers', a: true },
                      { l: 'Schedule appt', a: false },
                      { l: 'Phone call', a: false },
                      { l: 'Close', a: false },
                    ].map(s => (
                      <div key={s.l} className={`px-3 py-2 rounded-lg border text-xs font-medium flex items-center gap-2 ${s.a ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background/40 text-muted-foreground'}`}>
                        {s.a && <CheckCircle2 className="h-3.5 w-3.5" />}{s.l}
                      </div>
                    ))}
                  </div>
                </div>
                <button className="w-full bg-primary text-primary-foreground rounded-lg py-2.5 text-sm font-semibold">Save Activity</button>
              </div>
              <div className="px-5 py-3 border-t border-border bg-background/30 text-xs text-muted-foreground flex items-center gap-2">
                <ListChecks className="h-3.5 w-3.5" /> Every deal gets a next step — enforced at save
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section id="features" className="py-20 border-t border-border/30 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-center mb-4">Why Dealerships Choose <span className="text-primary">Pull</span> Over <span className="text-primary">Sales</span></h2>
          <p className="text-muted-foreground text-center mb-14 max-w-2xl mx-auto">Pull Over Sales sits on top of your existing CRM to make demos, follow-ups, stalled deals, and rep activity visible in real time.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Log Activity in Seconds', desc: 'Reps update deals in 10 seconds flat. Walkarounds, test drives, follow-ups — all tracked with two taps.' },
              { icon: BarChart3, title: 'Real-Time Floor Dashboard', desc: 'Managers see every deal, every rep, every next step. Color-coded urgency. No guesswork.' },
              { icon: Clock, title: 'Never Miss a Follow-Up', desc: 'Automated alerts for stalled deals, missed follow-ups, and hot opportunities ready to close.' },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                className="bg-card border border-border rounded-lg p-8 hover:border-primary/30 transition-colors"
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
                  <f.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{f.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Manager vs Rep Value */}
      <section id="roles" className="py-20 border-t border-border/30 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          <div className="bg-card border border-border rounded-lg p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div>
              <h3 className="font-display text-2xl font-bold">For Managers</h3>
            </div>
            <ul className="space-y-3 text-muted-foreground">
              {[
                { Icon: Eye, t: 'See the entire floor in 30 seconds' },
                { Icon: AlarmClock, t: "Know which deals are stalling before it's too late" },
                { Icon: LineChart, t: 'Coach from data, not guesswork' },
                { Icon: Activity, t: 'Track rep activity and follow-up discipline' },
                { Icon: Bell, t: 'Get alerted when customers need attention' },
              ].map(({ Icon, t }) => (
                <li key={t} className="flex items-start gap-3"><Icon className="h-4 w-4 text-primary mt-1 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="bg-card border border-border rounded-lg p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-primary" /></div>
              <h3 className="font-display text-2xl font-bold">For Sales Reps</h3>
            </div>
            <ul className="space-y-3 text-muted-foreground">
              {[
                { Icon: Zap, t: 'Log demos and activity in 10 seconds' },
                { Icon: ListChecks, t: 'See your daily action list at a glance' },
                { Icon: BellRing, t: 'Never forget a follow-up or next step' },
                { Icon: Trophy, t: 'Track your score and compete on the leaderboard' },
                { Icon: Target, t: 'Focus on selling, not data entry' },
              ].map(({ Icon, t }) => (
                <li key={t} className="flex items-start gap-3"><Icon className="h-4 w-4 text-primary mt-1 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 border-t border-border/30 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-center mb-14">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector line, md+ only */}
            <div className="hidden md:block absolute top-8 left-[16.66%] right-[16.66%] pointer-events-none">
              <svg viewBox="0 0 100 2" preserveAspectRatio="none" className="w-full h-0.5">
                <line x1="0" y1="1" x2="100" y2="1" stroke="hsl(var(--primary))" strokeWidth="0.5" strokeDasharray="2 2" opacity="0.4" />
              </svg>
            </div>
            {[
              { step: '01', title: 'Rep Logs Activity', desc: 'Walkaround, test drive, follow-up — logged in seconds from any device.' },
              { step: '02', title: 'Dashboard Updates Live', desc: 'Deals move. KPIs update. Managers see everything in real time.' },
              { step: '03', title: 'Automations Fire', desc: 'Follow-up reminders, stall alerts, and CSI routing happen automatically.' },
            ].map((s, i) => (
              <motion.div key={s.step} className="text-center relative z-10" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground text-2xl font-bold flex items-center justify-center mx-auto mb-5 ring-8 ring-background">{s.step}</div>
                <h3 className="text-xl font-semibold mb-3">{s.title}</h3>
                <p className="text-muted-foreground">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 border-t border-border/30 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <HelpCircle className="h-10 w-10 text-primary mx-auto mb-4" />
            <h2 className="font-display text-3xl font-bold mb-3">Frequently Asked Questions</h2>
            <p className="text-muted-foreground">Straight answers to what dealerships ask us most.</p>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-3">
            {[
              {
                q: 'Is this a CRM? Will it replace what we already use?',
                a: (
                  <>
                    No — PullOver Sales is a sales execution layer, not a CRM. Keep your DMS and CRM (we play nice with VinSolutions, DealerSocket, Elead, and others). We sit on top to surface every deal, demo, and follow-up in real time — so managers get a{' '}
                    <a href="#features" className="text-primary hover:underline font-medium">30,000-foot view of the floor</a> in under 30 seconds, and reps cut data entry by ~70%.
                  </>
                ),
              },
              {
                q: 'How fast is logging activity for reps?',
                a: (
                  <>
                    About 10 seconds per entry — two taps, no typing required for the common cases (walkaround, test drive, follow-up, next step). See it in the{' '}
                    <a href="#how-it-works" className="text-primary hover:underline font-medium">How It Works</a> flow. Compared to the 2–4 minutes a typical CRM note takes, a rep working 8 deals a day saves roughly 25 minutes daily — over 2 hours a week back on the floor.
                  </>
                ),
              },
              {
                q: 'Is it multi-dealership? Can I run a group on one account?',
                a: (
                  <>
                    Yes — multi-tenant by design from day one. We support unlimited rooftops on a single account, with per-store dashboards, leaderboards, and pipelines, plus a group-level rollup for ownership. Three role tiers (rep, manager, admin) — see the breakdown in{' '}
                    <a href="#roles" className="text-primary hover:underline font-medium">For Managers vs For Reps</a>. Adding a new rooftop takes under 5 minutes.
                  </>
                ),
              },
              {
                q: 'What automations run in the background?',
                a: (
                  <>
                    Six core automations run 24/7: (1) stalled-deal alerts at the 24-hour no-activity mark, (2) missed follow-up reminders within 15 minutes of the scheduled time, (3) "every deal needs a next step" enforcement on save, (4) hot-deal flags when a customer hits 3+ touches in 48 hours, (5) CSI / review-request routing 24 hours after delivery, and (6) end-of-day manager digest at 6pm. Walk through the trigger flow in{' '}
                    <a href="#how-it-works" className="text-primary hover:underline font-medium">How It Works → Automations Fire</a>.
                  </>
                ),
              },
              {
                q: 'How long does it take to roll out to my team?',
                a: (
                  <>
                    Live on signup — under 5 minutes from account creation to first deal logged. Zero install (it runs in the browser and as a PWA).{' '}
                    <Link to="/login" className="text-primary hover:underline font-medium">Book a demo</Link>{' '}or{' '}
                    <Link to="/login" className="text-primary hover:underline font-medium">jump into the dashboard</Link>{' '}now. Typical timeline: day 1 = managers + reps onboarded and logging, week 1 = full floor adoption, week 2 = first measurable lift in follow-up rate (most stores see +20–30%).
                  </>
                ),
              },
              {
                q: 'Does it work offline on the lot?',
                a: (
                  <>
                    Yes — full offline mode via PWA. Reps can log walkarounds and test drives anywhere on the lot, even in dead zones (back row, basement service drive, tented overflow). Entries queue locally and auto-sync within 2 seconds of reconnecting — see it in action on the{' '}
                    <a href="#features" className="text-primary hover:underline font-medium">Log Activity in Seconds</a> card above.
                  </>
                ),
              },
              {
                q: 'How can dealerships track stalled deals faster?',
                a: (
                  <>
                    Pull Over Sales auto-flags any deal with no logged activity in 24 hours and surfaces it on the{' '}
                    <a href="#product" className="text-primary hover:underline font-medium">Manager Dashboard</a> with a red stall badge. Managers can nudge the rep in one tap, and the rep gets a push notification on their phone with the deal pre-loaded — no inbox digging, no spreadsheet sweep.
                  </>
                ),
              },
              {
                q: 'What is the best sales tracking app for car dealerships?',
                a: (
                  <>
                    The right tool depends on your floor: CRMs (VinSolutions, DealerSocket, Elead) are great at storing customer data but weak at real-time execution visibility. Pull Over Sales is purpose-built for the activity layer — demos, follow-ups, next steps, stall alerts, and rep scoring — and sits on top of whatever CRM you already use. See the{' '}
                    <a href="#features" className="text-primary hover:underline font-medium">Why Dealerships Choose</a> section for the trade-offs.
                  </>
                ),
              },
              {
                q: 'How do automotive sales managers monitor follow-up discipline?',
                a: (
                  <>
                    Every logged activity requires a next step before it can be saved, and every deal exposes a follow-up rate on the{' '}
                    <a href="#product" className="text-primary hover:underline font-medium">leaderboard</a>. Managers see per-rep follow-up % at a glance, drill into missed steps, and get a 6pm daily digest emailed automatically — so coaching conversations are based on data, not memory.
                  </>
                ),
              },
            ].map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`item-${i}`}
                className="bg-card border border-border rounded-lg px-5 data-[state=open]:border-primary/30"
              >
                <AccordionTrigger className="text-left font-semibold hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="text-center text-sm text-muted-foreground mt-8">
            Still have questions?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Book a demo
            </Link>{' '}
            and we will walk your team through it.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-border/30">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Shield className="h-10 w-10 text-primary mx-auto mb-6" />
          <h2 className="font-display text-3xl font-bold mb-4">Ready to See Your Sales Floor Clearly?</h2>
          <p className="text-muted-foreground mb-8">Join dealerships that stopped guessing and started closing.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login?tab=demo"><Button size="lg" className="text-base px-8">View Demo</Button></Link>
            <Link to="/login?tab=real"><Button size="lg" variant="outline" className="text-base px-8">Secure Login</Button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/30 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pb-8 border-b border-border/30">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" aria-label="PullOver.App home" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><Logo variant="full" className="h-12 object-contain mb-3" /></Link>
              <p className="text-xs text-muted-foreground max-w-xs">Sales execution layer for modern dealerships. Not another CRM.</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-muted-foreground hover:text-primary transition-colors">Features</a></li>
                <li><Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</Link></li>
                <li><Link to="/login?tab=demo" className="text-muted-foreground hover:text-primary transition-colors">Demo</Link></li>
                <li><Link to="/roadmap" className="text-muted-foreground hover:text-primary transition-colors">Roadmap</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:hello@pullover.app" className="text-muted-foreground hover:text-primary transition-colors">Contact</a></li>
                <li><a href="#roles" className="text-muted-foreground hover:text-primary transition-colors">For Managers</a></li>
                <li><a href="#roles" className="text-muted-foreground hover:text-primary transition-colors">For Reps</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms</Link></li>
                <li><Link to="/refunds" className="text-muted-foreground hover:text-primary transition-colors">Refunds</Link></li>
              </ul>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground pt-6">&copy; {new Date().getFullYear()} PullOver.App — All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
